import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * JotformEmbed Component
 * 
 * A React component for embedding Jotform forms with prefill support and submission polling.
 * Designed for contract signing workflows (MSA, BAA, ANC) with session tracking.
 * 
 * @component
 * @example
 * // Basic usage with contract signing
 * <JotformEmbed
 *   formId="260466314214046"
 *   sessionId="unique-session-id-123"
 *   contractType="msa"
 *   signerName="John Doe"
 *   signerEmail="john@company.com"
 *   organizationName="Acme Health"
 *   onSubmissionReceived={(submission) => console.log('Form submitted:', submission)}
 * />
 * 
 * @param {Object} props - Component props
 * 
 * REQUIRED PROPS:
 * @param {string} props.formId - Jotform form ID (e.g., "260466314214046")
 * @param {string} props.sessionId - Unique session ID for tracking this form instance
 * 
 * OPTIONAL PREFILL PROPS:
 * @param {string} props.contractType - Contract type: "msa", "baa", or "anc" (default: "msa")
 * @param {string} props.signerName - Name of the person signing the contract
 * @param {string} props.signerEmail - Email of the signer (supports + in email via {plusSign} token)
 * @param {string} props.organizationName - Organization name for the contract
 * @param {string} props.contractAttemptId - Optional attempt tracking ID
 * 
 * CONFIGURATION PROPS:
 * @param {string} props.apiBase - Jotform API base URL (default: "https://api.jotform.com")
 *   Options: "https://api.jotform.com" (Standard)
 *            "https://hipaa-api.jotform.com" (HIPAA)
 *            "https://eu-api.jotform.com" (EU)
 * @param {string} props.sessionFieldName - Field name in Jotform for session tracking (default: "session_id")
 * @param {number} props.height - iFrame height in pixels (default: 720)
 * @param {boolean} props.autoScroll - Enable auto-scroll on form load (default: true)
 * @param {boolean} props.enablePolling - Enable automatic submission polling (default: false)
 * @param {number} props.pollingInterval - Polling interval in milliseconds (default: 5000)
 * 
 * CALLBACK PROPS:
 * @param {Function} props.onSubmissionReceived - Called when a submission is detected
 *   Receives submission object: { id, answers, created_at, status, ... }
 * @param {Function} props.onError - Called when an error occurs during polling
 *   Receives error object: { message, error }
 * @param {Function} props.onLoad - Called when the iframe loads successfully
 * 
 * FIELD MAPPING:
 * Ensure your Jotform fields have these unique names:
 * - session_id (or custom via sessionFieldName prop)
 * - contract_type
 * - contract_attempt_id
 * - signer_name
 * - signer_email
 * - organization_name
 * 
 * BACKEND REQUIREMENTS:
 * The component expects these backend endpoints:
 * - POST /api/jotform/poll-submissions - Poll for form submissions
 *   Request body: { form_id, session_id, session_field_name, api_base, api_key }
 *   Response: { submissions: [...], count: number }
 */
const JotformEmbed = ({
  // Required
  formId,
  sessionId,
  
  // Prefill values
  contractType = 'msa',
  signerName = '',
  signerEmail = '',
  organizationName = '',
  contractAttemptId = '',
  
  // Configuration
  apiBase = 'https://api.jotform.com',
  sessionFieldName = 'session_id',
  height = 720,
  autoScroll = true,
  enablePolling = false,
  pollingInterval = 5000,
  
  // Callbacks
  onSubmissionReceived = null,
  onError = null,
  onLoad = null,
}) => {
  const [embedUrl, setEmbedUrl] = useState('');
  const [pollResult, setPollResult] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const pollingIntervalRef = useRef(null);
  const iframeRef = useRef(null);

  /**
   * Encodes values for Jotform URL parameters
   * Special handling for email addresses with + sign using {plusSign} token
   */
  const encodeValueForJotform = (key, value) => {
    if (key === 'signer_email') {
      const tokenized = String(value).replace(/\+/g, '{plusSign}');
      return encodeURIComponent(tokenized).replace(/%7BplusSign%7D/g, '{plusSign}');
    }
    return encodeURIComponent(String(value));
  };

  /**
   * Builds the Jotform embed URL with prefill parameters
   */
  const buildEmbedUrl = () => {
    if (!formId) {
      console.error('Form ID is required');
      return '';
    }

    const entries = [];
    
    // Add all prefill values
    if (sessionId) entries.push([sessionFieldName, sessionId]);
    if (contractType) entries.push(['contract_type', contractType]);
    if (contractAttemptId) entries.push(['contract_attempt_id', contractAttemptId]);
    if (signerName) entries.push(['signer_name', signerName]);
    if (signerEmail) entries.push(['signer_email', signerEmail]);
    if (organizationName) entries.push(['organization_name', organizationName]);

    // Build query string
    const queryParts = entries.map(([k, v]) => `${k}=${encodeValueForJotform(k, v)}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

    const url = `https://form.jotform.com/${formId}${queryString}`;
    setEmbedUrl(url);
    console.log('Embed URL generated:', url);
    return url;
  };

  /**
   * Polls the backend for form submissions matching the session ID
   */
  const pollSubmissions = async () => {
    if (!formId || !sessionId) {
      console.error('Form ID and Session ID are required for polling');
      return;
    }

    try {
      const response = await fetch('http://localhost:8001/api/jotform-sign-poc/poll/', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': '*/*',
        },
        body: JSON.stringify({
          form_id: formId,
          session_id: sessionId,
          session_field_name: sessionFieldName,
          contract_type: contractType,
          api_key: '',
          api_base_url: apiBase,
          limit: 100,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setPollResult(data);

      console.log('Poll response:', data);

      // Check if we have a match with signature
      if (data.success && data.matched && data.matches && data.matches.length > 0) {
        const latestMatch = data.matches[0];
        
        // Verify session ID matches
        if (latestMatch.matched_session_id === sessionId) {
          // Check if document is signed
          if (latestMatch.has_signature_answer) {
            console.log('Contract signed! Submission ID:', latestMatch.submission_id);

            // Call callback if provided
            if (onSubmissionReceived) {
              onSubmissionReceived({
                id: latestMatch.submission_id,
                created_at: latestMatch.created_at,
                status: latestMatch.status,
                has_signature: latestMatch.has_signature_answer,
                session_id: latestMatch.matched_session_id,
              });
            }

            // Stop polling after finding signed submission
            if (isPolling) {
              stopPolling();
            }
          } else {
            console.log('Submission found but not signed yet');
          }
        } else {
          console.log('Session ID mismatch:', latestMatch.matched_session_id, 'vs', sessionId);
        }
      } else {
        console.log('No matching signed submissions found yet');
      }
    } catch (error) {
      const errorMsg = `Poll failed: ${error.message}`;
      console.error(errorMsg, error);
      setPollResult({ error: errorMsg });

      if (onError) {
        onError({ message: errorMsg, error });
      }
    }
  };

  /**
   * Starts automatic polling
   */
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Already polling

    setIsPolling(true);
    console.log(`Auto-polling started (every ${pollingInterval / 1000}s)`);

    // Poll immediately
    pollSubmissions();

    // Set up interval
    pollingIntervalRef.current = setInterval(() => {
      pollSubmissions();
    }, pollingInterval);
  };

  /**
   * Stops automatic polling
   */
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
    console.log('Auto-polling stopped');
  };

  /**
   * Toggles polling on/off
   */
  const togglePolling = () => {
    if (isPolling) {
      stopPolling();
    } else {
      startPolling();
    }
  };

  // Build embed URL on mount and when dependencies change
  useEffect(() => {
    const url = buildEmbedUrl();
    if (url && iframeRef.current) {
      iframeRef.current.src = url;
    }
  }, [formId, sessionId, contractType, signerName, signerEmail, organizationName, contractAttemptId, sessionFieldName]);

  // Start polling if enabled
  useEffect(() => {
    if (!enablePolling) return;

    console.log(`Starting auto-polling (every ${pollingInterval / 1000}s)`);
    setIsPolling(true);

    // Poll immediately on mount
    pollSubmissions();

    // Set up interval for continuous polling
    const intervalId = setInterval(() => {
      console.log('Polling interval triggered...');
      pollSubmissions();
    }, pollingInterval);

    pollingIntervalRef.current = intervalId;

    // Cleanup on unmount
    return () => {
      console.log('Cleaning up polling interval');
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [enablePolling, pollingInterval, formId, sessionId]);

  // Handle iframe load
  const handleIframeLoad = () => {
    if (onLoad) {
      onLoad();
    }
  };

  return (
    <div className="container-fluid p-0" style={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      {/* Embedded Form */}
      <div className="bg-white border rounded-3 shadow-sm overflow-hidden" style={{ 
        background: 'white', 
        border: '1px solid #ddd', 
        borderRadius: 8, 
        overflow: 'hidden', 
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' 
      }}>
        <iframe
          ref={iframeRef}
          className="w-100 border-0 d-block"
          title="Jotform Contract"
          style={{ height: `${height}px`, width: '100%', border: 'none', display: 'block' }}
          frameBorder="0"
          scrolling={autoScroll ? 'yes' : 'no'}
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
};

JotformEmbed.propTypes = {
  // Required
  formId: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  
  // Prefill values
  contractType: PropTypes.oneOf(['msa', 'baa', 'anc']),
  signerName: PropTypes.string,
  signerEmail: PropTypes.string,
  organizationName: PropTypes.string,
  contractAttemptId: PropTypes.string,
  
  // Configuration
  apiBase: PropTypes.oneOf([
    'https://api.jotform.com',
    'https://hipaa-api.jotform.com',
    'https://eu-api.jotform.com',
  ]),
  sessionFieldName: PropTypes.string,
  height: PropTypes.number,
  autoScroll: PropTypes.bool,
  enablePolling: PropTypes.bool,
  pollingInterval: PropTypes.number,
  
  // Callbacks
  onSubmissionReceived: PropTypes.func,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
};

export default JotformEmbed;
